<template>
    <div class="MoreItemsCardGrid w-full">
        <CMasonryGrid class="w-[calc(100%+32px)]" full-width>
            <template v-for="(item, idx) in data" :key="idx">
                <div v-if="isItemVisible(idx)">
                    <!-- Skills need special handling as CMasonryCard doesn't support them -->
                    <SkillCard v-if="item.type === 'skills'" badge-main :skill="item.item" />

                    <!-- All other types use CMasonryCard -->
                    <CMasonryCard
                        v-else
                        :data="item.item"
                        :type="mapTypeToCardType(item.type)"
                    />
                </div>
            </template>

            <MoreItemsCard
                v-if="moreItemsCardVisible"
                :button-text="showMoreItemsLabel"
                :items-left-count="moreItemsCount"
                :items-left-label="moreLabel"
                variant="primary"
                @click-button="showMoreInSection"
            />
        </CMasonryGrid>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, useAttrs } from 'vue'

import type { Job } from '#shared/types/Job'
import type { Org } from '#shared/types/Org'
import { Project } from '#shared/types/Project'
import { Skill } from '#shared/types/Skill'
import { MentorProfile, UserProfile, VolunteerProfile } from '#shared/types/User'
import MoreItemsCard from '@/components/common/MoreItemsCard.vue'
import CMasonryCard from '@/components/ui/CMasonryCard.vue'
import CMasonryGrid from '@/components/ui/CMasonryGrid.vue'
import SkillCard from '@/components/skills/SkillCard.vue'

export type OpportunityItem = {
    type: string
    item: Org | Skill | Project | Job | UserProfile | MentorProfile | VolunteerProfile
}

// Map collection item types to CMasonryCard types
const mapTypeToCardType = (type: string): 'job' | 'project' | 'org' | 'mentor' | 'volunteer' | 'user' | undefined => {
    const typeMap: Record<string, 'job' | 'project' | 'org' | 'mentor' | 'volunteer' | 'user'> = {
        'jobs': 'job',
        'projects': 'project',
        'orgs': 'org',
        'mentors': 'mentor',
        'profiles': 'mentor',
        'volunteers': 'volunteer'
    }
    return typeMap[type]
}

const ITEMS_IN_ROW_COUNT = 4

const props = defineProps({
    lang: {
        type: String as PropType<'sk' | 'en'>,
        default: 'sk'
    },
    items: {
        type: Array as PropType<OpportunityItem[]>,
        required: true
    },
    itemsType: {
        type: String as PropType<'orgs' | 'individuals' | 'opportunities'>,
        default: 'opportunities'
    },
    itemsGender: {
        type: String as PropType<'he' | 'she' | 'it' | 'default'>,
        default: 'default'
    },
    /**
     * Visible items
     *
     * How many items will be show by default
     */
    visibleItems: {
        type: Number,
        default: 3
    },
    /**
     * Show more rows
     *
     * How many rows will be show after click on show more
     */
    showMoreRows: {
        type: Number,
        default: 4
    }
})

const { t } = useI18n()
const attrs = useAttrs()
const visibleItemsValue = ref(props.visibleItems)
const data = ref<OpportunityItem[]>([])

// Compute if "show more" should open in new tab
const shouldOpenMoreInNewTab = computed(() => Boolean(attrs.route) && attrs.target === '_blank')

/**
 * Calculate more items count
 *
 * Calculate how many items is not listed
 */
const moreItemsCount = computed(() => data.value.length - visibleItemsValue.value)

/**
 * Show more label
 */
const moreLabel = computed(() => t(`common.entities.${props.itemsType}`, moreItemsCount.value))

/**
 * Show more card visible
 *
 * Detect if show more card is needed to show
 */
const moreItemsCardVisible = computed(
    () => data.value.length > props.visibleItems + 1 && visibleItemsValue.value < data.value.length
)

/**
 * Calculate items
 *
 * Calculate how many items will be show after click on show more
 */
const showMoreItemsCount = computed(() => props.showMoreRows * ITEMS_IN_ROW_COUNT)

/**
 * Show more items label
 */
const showMoreItemsLabel = computed(() =>
    t(`common.buttons.showMore.${props.itemsGender}`, showMoreItemsCount.value)
)

watch(
    () => props.visibleItems,
    (value) => {
        visibleItemsValue.value = value
    }
)

// Watch for items changes to update data
watch(
    () => props.items,
    (newItems) => {
        if (!newItems) {
            data.value = []
            return
        }
        data.value = newItems.filter((item) => {
            if (item.item) return true
            return false
        })
    },
    { immediate: true, deep: true }
)

/**
 * Check if item will be visible
 * Calculate how many items will be visible include card show more
 *
 * +1 => card show more
 *
 * @param idx
 */
const isItemVisible = (idx: number) => {
    return data.value.length > props.visibleItems + 1 ? idx < visibleItemsValue.value : true
}

/**
 * Show more card
 *
 * -1 => show more card by default
 *
 * Decide if last card will be show more or basic opportunity card
 */
const showMoreInSection = () => {
    if (shouldOpenMoreInNewTab.value) {
        window.open(attrs.route as string, '_blank')
    } else if (visibleItemsValue.value + showMoreItemsCount.value === data.value.length - 1) {
        // calculate if all items will be show
        visibleItemsValue.value += showMoreItemsCount.value + 1
    } else {
        visibleItemsValue.value += showMoreItemsCount.value
    }
}
</script>
